import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ChatPanel from './ChatPanel';
import {
  toggleChatWidget,
  clearChatHistoryAction,
  getSuggestionsAction,
  initChatbotAction,
  setVerificationErrorContext,
  clearVerificationErrorContext,
} from '../../redux/actions/chatbot';
import './style.scss';
import ChatTooltip from './ChatTooltip';
import { useIdleTimer } from 'react-idle-timer';
import imgURL from '../../lib/imgURL';

// CSS selectors that indicate a visible form-field validation error on the page
const FORM_ERROR_SELECTORS = [
  '.error-field',
  '.error-field .error',
  '.error-message',
  '.error-text',
  '.input-with-error',
];

/** Gather visible form-field error text from the DOM */
const gatherDomErrorTexts = (): string[] => {
  const texts: string[] = [];
  FORM_ERROR_SELECTORS.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (htmlEl.offsetParent !== null) {
        const text = htmlEl.textContent?.trim();
        if (text) texts.push(text);
      }
    });
  });
  return [...new Set(texts)]; // dedupe
};

const ChatWidget: FC<any> = (props): JSX.Element => {
  const {
    isOpen,
    messageCount,
    toggle,
    clearHistory,
    loadSuggestions,
    initChatbot,
    ticketCount,
    onboardingReducer,
  } = props;

  const inited = useRef(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [formFieldErrors, setFormFieldErrors] = useState<string[]>([]);
  const location = useLocation();
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    loadSuggestions(props?.stepperDetails?.data?.current_step);
  }, [props?.stepperDetails?.data?.current_step]);

  // Fetch history + ticket count once on mount, before user ever opens the bot
  useEffect(() => {
    if (!inited.current) {
      inited.current = true;
      initChatbot();
    }
  }, []);

  // Idle timer configuration: set tooltip visible after 5 seconds of inactivity
  useIdleTimer({
    onIdle: () => setIsTooltipVisible(true),
    timeout: 30000,
    debounce: 500
  });

  useEffect(() => {
    setIsTooltipVisible(false);
  }, [isOpen]);

  const isUserVerificationRoute = location.pathname.includes('/user-verification');

  // Scan onboarding reducer for any sub-state that has failed with an error message
  const onboardingErrorMessages: string[] = useMemo(() => {
    if (!onboardingReducer) return [];
    const state = onboardingReducer;
    const errors: string[] = [];

    Object.keys(state).forEach((key) => {
      const subState = state[key];
      if (
        subState &&
        typeof subState === 'object' &&
        subState.isFailure &&
        subState.errorMessage
      ) {
        errors.push(subState.errorMessage);
      }
    });

    return errors;
  }, [onboardingReducer]);

  // ── Form field error detection via MutationObserver ──────────────────
  // Watches the DOM for inline form-field validation errors (e.g. "This field is required")
  const scanAndUpdateFormErrors = () => {
    const errors = gatherDomErrorTexts();
    setFormFieldErrors(errors);
    return errors.length > 0;
  };

  // Set up / tear down MutationObserver when route changes
  useEffect(() => {
    if (!isUserVerificationRoute) return;

    let debounceTimer: ReturnType<typeof setTimeout>;

    observerRef.current = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (scanAndUpdateFormErrors()) {
          setIsTooltipVisible(true);
        }
      }, 300); // debounce to avoid firing on every keystroke
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      clearTimeout(debounceTimer);
    };
  }, [isUserVerificationRoute]);

  // Also do an immediate scan when the route path changes
  useEffect(() => {
    if (isUserVerificationRoute && scanAndUpdateFormErrors()) {
      setIsTooltipVisible(true);
    }
  }, [location.pathname]);

  // Show tooltip for API-level onboarding failures too
  useEffect(() => {
    if (isUserVerificationRoute && onboardingErrorMessages.length > 0) {
      setIsTooltipVisible(true);
    }
  }, [onboardingErrorMessages, isUserVerificationRoute]);

  const hasVerificationError = isUserVerificationRoute && (
    formFieldErrors.length > 0 || onboardingErrorMessages.length > 0
  );

  /**
   * Gathers all errors (DOM + API) as a combined context object
   * for sending invisibly to the chatbot server.
   */
  const gatherErrorContext = () => ({
    errors: [...formFieldErrors, ...onboardingErrorMessages],
    route: location.pathname,
  });

  const hideTooltip = () => setIsTooltipVisible(false);

  /**
   * When the FAB is clicked and there's a verification error, dispatch the
   * error context to Redux so ChatPanel can auto-send it to the bot.
   * The error message itself is NOT shown in the UI or saved in history.
   */
  const handleFabClick = () => {
    if (!isOpen && hasVerificationError) {
      // Dispatch error context — ChatPanel will pick it up and auto-send
      props.dispatchSetVerificationErrorContext(gatherErrorContext());
    }
    props.toggle();
  };

  useEffect(() => {
    if (isOpen && hasVerificationError) {
      props.dispatchSetVerificationErrorContext(gatherErrorContext(), true);
    }
  }, [isOpen,hasVerificationError]);

  return (
    <div className='chat-widget-container'>
      <ChatPanel
        isOpen={isOpen}
        onClose={toggle}
        onClearHistory={clearHistory}
        ticketCount={ticketCount}
        hideTooltip={hideTooltip}
      />
      <ChatTooltip
        text="Hi, do you need any help?"
        visible={!isOpen && isTooltipVisible}
        position="top"
        onClose={hideTooltip}
      />
      <button
        className={`chat-fab ${isOpen ? 'chat-fab-active' : ''
          }`}
        onClick={handleFabClick}
        title={isOpen ? 'Close Guide' : 'Guide'}
        aria-label={isOpen ? 'Close Guide' : 'Open Guide'}
      >
        <img src={imgURL['guide-mini-logo']} alt="Guide" />
        Ask Guide
      </button>
      {/* {!isOpen && isTooltipVisible && (
        <span className='chat-fab-badge'>{messageCount}</span>
      )} */}
    </div>
  );
};

const mapStateToProps = ({ chatbotReducer, onboardingReducer }: any) => ({
  isOpen: chatbotReducer.isOpen,
  messageCount: Math.max(chatbotReducer.totalHistoryCount || 0, chatbotReducer.messages.length),
  stepperDetails: onboardingReducer?.stepperDetails,
  onboardingReducer,
});

const mapDispatchToProps = (dispatch: any) => ({
  toggle: () => dispatch(toggleChatWidget()),
  clearHistory: () => dispatch(clearChatHistoryAction()),
  loadSuggestions: (current_step?: string) => dispatch(getSuggestionsAction(current_step)),
  initChatbot: () => dispatch(initChatbotAction()),
  dispatchSetVerificationErrorContext: (ctx: { errors: string[]; route: string }, isAlreadyOpened: boolean) =>
    dispatch(setVerificationErrorContext(ctx, isAlreadyOpened)),
  dispatchClearVerificationErrorContext: () => dispatch(clearVerificationErrorContext()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ChatWidget);
